import { create } from "zustand";
import type { GetPlaylistsResult } from "./modal/AddToPlaylistModal";
import { fetchKmsData } from "@kaltura/mediaspace-shared-utils";

type PlaylistsFetcher = () => GetPlaylistsResult;
type PlaylistsRequestData = Record<string, unknown>;

interface PlaylistsListState {
    /**
     * contains fetchKmsData
     * when null the store refetches the playlists
     * when not null, refetch is skipped
     */
    fetcher: PlaylistsFetcher | null;

    /**
     * function for fetching playlists
     * @param url API endpoint used to load playlists.
     * @param data Optional object to send as POST data (e.g. entryIds).
     */
    getPlaylists: (url: string, data?: PlaylistsRequestData) => GetPlaylistsResult;

    /**
     * Clears the current fetcher and key so the next
     * getPlaylists call will create a fresh Suspense resource.
     */
    invalidate: () => void;

    /**
     * bumped whenever playlists are updated via the add-to-playlist flow.
     * Other consumers (e.g. PlaylistsTab) can include this in their fetch deps
     * to force a refetch when it changes.
     */
    playlistsUpdateToken: number;
}

/**
 * store for personal/channel playlists list fetching
 * used by the app in locations that handle personal/channel playlists updating,
 * such as channel edit and media page
 */
export const usePlaylistsListStore = create<PlaylistsListState>((set, get) => ({
    fetcher: null,
    playlistsUpdateToken: 0,
    getPlaylists: (url: string, data?: PlaylistsRequestData) => {
        let { fetcher } = get();

        if (!fetcher) {
            fetcher = fetchKmsData(url, data ?? {});
            set({ fetcher });
        }

        return fetcher();
    },
    invalidate: () => {
        set((state) => ({
            fetcher: null,
            playlistsUpdateToken: state.playlistsUpdateToken + 1,
        }));
    },
}));
